import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';

type ToastKind = 'ok' | 'error';

/** One action in a toast, e.g. Undo for a reversible change (UX audit #26). */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  action?: ToastAction;
}

type PushToast = (message: string, kind?: ToastKind, action?: ToastAction) => void;

const ToastContext = createContext<PushToast>(() => undefined);

/** Transient system messages at the end corner. They appear and leave; one with an action stays longer. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const dismiss = useCallback((id: number) => setToasts((current) => current.filter((toast) => toast.id !== id)), []);
  const push = useCallback<PushToast>(
    (message, kind = 'ok', action) => {
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { id, kind, message, action }]);
      setTimeout(() => dismiss(id), kind === 'error' || action ? 8000 : 4000);
    },
    [dismiss],
  );
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="fs-toasts" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`fs-toast fs-toast--${toast.kind}`} role={toast.kind === 'error' ? 'alert' : undefined}>
            <span className="fs-toast__message">{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                className="fs-toast__action"
                onClick={() => {
                  dismiss(toast.id);
                  toast.action!.onClick();
                }}
              >
                {toast.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Push a transient message. A no-op outside ToastProvider (tests). */
export const useToast = () => useContext(ToastContext);
